/* ============================================================================
   Shape hub — SECTION BEHAVIOUR (one file, all ten shapes)
   ----------------------------------------------------------------------------
   A shape hub is every piece of jewelry cut in one diamond shape: one grid per
   family, each with its own View More, plus the thing this page type exists for
   — a Lab Grown / Natural switch that re-filters every shelf, every count and
   every door at once, without leaving the page.

   Forked from ps-labhub26.js (the lab hub, 2026-08-31) rather than shared with
   it: that page has no origin axis at all — everything on it is lab — so every
   function here that touches origin would be dead code there.

   The shared collection shell (ps-collection26.js) still runs and still owns
   Favorite Picks, the reviews marquee, the FAQ accordion and the scroll reveals.
   It leaves the product grid alone because there is no #cg-grid here (it returns
   early at `if (!grid) return`), so this file adds behavior rather than
   replacing any.

   Globals this file expects, all set before it runs:
     window.PS_COLLECTION  — assets/data/products.js  (the shape's snapshot)
     window.PS_SHAPEHUB    — assets/data/families.js  (build_shape_hub_data.py)
     window.PS_PAGE.hub    — card labels + section sizing
     window.PS_PAGE.picks  — cores + a parallel `origins` array

   Cards carry no compare or wishlist button: both are wired by the shell's grid
   pipeline, which does not run here. The filterable views behind the doors at
   the foot of the page keep them.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.PS_PAGE || {};
  var HUB = window.PS_SHAPEHUB || { families: [], totals: {} };
  var DATA = window.PS_COLLECTION || { products: [] };
  var HUBCFG = CFG.hub || {};

  var PORT = window.PS_PORT || null;
  var DS = CFG.ds || '../_ds';
  function dsAsset(name) { return PORT ? PORT.base + 'ps-26-' + name : DS + '/assets/' + name; }

  var ROWS = HUBCFG.rows || 2;                 /* rows revealed per View More click */
  var SHAPE_LABEL = {round:'Round', princess:'Princess', emerald:'Emerald', oval:'Oval', cushion:'Cushion', marquise:'Marquise', pear:'Pear', asscher:'Asscher', radiant:'Radiant', heart:'Heart'};
  var CARD_SHAPES = CFG.shapeIcons || ['round', 'princess', 'emerald', 'oval', 'cushion'];
  var ORIGIN_LABEL = { natural: 'Natural Diamond', lab: 'Lab-Grown Diamond' };
  /* Platinum has no render of its own and never will — it reads as white gold in a
     photograph — so it points at the white images, exactly as the shell does. */
  var METALS = [
    {key:'white',    src:'white', code:'WG', label:'14K White Gold',  fill:'var(--ps-metal-white)',  ink:'var(--ps-navy)'},
    {key:'yellow',   src:'yellow',code:'YG', label:'14K Yellow Gold', fill:'var(--ps-metal-yellow)', ink:'var(--ps-bg)'},
    {key:'rose',     src:'rose',  code:'RG', label:'14K Rose Gold',   fill:'var(--ps-metal-rose)',   ink:'var(--ps-bg)'},
    {key:'platinum', src:'white', code:'PT', label:'Platinum',        fill:'var(--ps-metal-plat)',   ink:'var(--ps-navy)'}
  ];
  var BLANK_IMG = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  var ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

  var BY_ID = {};
  for (var i = 0; i < DATA.products.length; i++) BY_ID[DATA.products[i].id] = DATA.products[i];

  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  /* PSMoney (layout/ps26): the shopper's currency, never a hard-coded '$' (owner 2026-10-05). */
  function money(n) { return window.PSMoney ? PSMoney.fmt(n, 2) : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  function num(n) { return Number(n).toLocaleString('en-US'); }
  /* Not anchored to the start: some titles lead with an adjective and were losing
     their carat weight entirely (the same fix the shell carries). */
  function parseCarat(t) { var m = t.match(/([\d.]+(?:-[\d.]+)?)\s*CT\b/i); return m ? m[1].replace('-', '–') : ''; }

  /* A card is named from the shelf it sits in, not by matching the title against a
     family table. The shelf already IS the family, so "Round Bridal Set" needs no
     guessing, and a title that names two shapes keeps both. The shape is never said
     twice: on a shape page the label already opens with it, which is the defect that
     printed "Princess Princess Cut Diamond Jewelry" on 347 cards. */
  /* A combined-mode page has one pseudo-shelf, so there is no family label to name a card
     from and every card read "Heart & Round Piece". The shell's own naming table knows
     what the piece is, so the noun comes from the title instead. */
  var NAME_FAMILIES = (CFG.naming && CFG.naming.families) || [];
  function nounFor(p, label) {
    if (label !== 'Piece') return label;
    for (var i = 0; i < NAME_FAMILIES.length; i++) {
      if (new RegExp(NAME_FAMILIES[i][0], 'i').test(p.title)) return NAME_FAMILIES[i][1];
    }
    return label;
  }
  /* The store writes asscher as "Ascher" in 84 product titles. That is fine in the data
     and wrong on a card, where it reads as a typo on a page headed Asscher, so the shape
     word is corrected on its way out of the title. */
  var SPELLING = [[/\bAscher\b/gi, 'Asscher']];
  function cardName(p, label) {
    label = nounFor(p, label);
    var m = /CT\s+(.+?)\s+Cut\b/i.exec(p.title);
    var shape = m ? m[1] : (SHAPE_LABEL[p.shape] || '');
    if (!shape) return label;
    for (var s = 0; s < SPELLING.length; s++) shape = shape.replace(SPELLING[s][0], SPELLING[s][1]);
    var head = shape.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp('^' + head + '\\b', 'i').test(label)) return label;
    return shape + ' ' + label;
  }
  /* The spec line says what the stone is, because on this page that is the axis the
     shopper is switching on. It comes from the product's own origin field, never from
     the words in the title: 27 lab princess titles do not carry "lab" at all (D22). */
  function specLine(p) {
    var carat = parseCarat(p.title);
    var stone = p.origin === 'lab' ? 'Lab Grown Diamonds' : 'Natural Diamonds';
    return carat ? carat + ' CT · ' + stone : stone;
  }
  function starsHTML(r) {
    var full = Math.round(r), out = '';
    for (var i = 0; i < 5; i++) out += '<svg viewBox="0 0 24 24"' + (i < full ? '' : ' style="opacity:.28"') + '><path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z"/></svg>';
    return out;
  }
  function priceHTML(p) {
    var off = (p.priceWas && p.priceWas > p.priceNow && p.pctOff) ? '<span class="off">−' + p.pctOff + '%</span>' : '';
    return '<span class="price"><span class="now">' + money(p.priceNow) + '</span>' +
      (p.priceWas ? '<span class="was">' + money(p.priceWas) + '</span>' : '') + off + '</span>';
  }
  function ratingHTML(p) {
    return (p.rating != null)
      ? '<span class="pcard-rating"><span class="stars">' + starsHTML(p.rating) + '</span>' + p.rating.toFixed(1) + '</span>'
      : '';
  }
  /* A shelf of one metal reads as one product repeated, so each card opens on a metal
     drawn 60% white / 20% yellow / 20% rose. Derived from the product's rank, like the
     shell's, so the mix is identical on every reload and review screenshots compare. */
  function defaultMetal(p) {
    if (p.images.source !== 'render-drive') return 'white';
    var r = (p.rank * 2654435761 % 4294967296) % 100;
    var metal = r < 60 ? 'white' : (r < 80 ? 'yellow' : 'rose');
    return p.images.byColor[metal] ? metal : 'white';
  }
  function cardHTML(p, label) {
    var name = cardName(p, label);
    /* Owner review 2026-09-10 (C-02): "leave the same layout, just adjust the cards to
       look like our new collection cards". With cards26 on, the shell's approved card
       renders the tile (bordered card, spec + price panel, swatch rail, rating, no
       shape row, no origin badge) and only the hub's family-aware name is kept. */
    var shell = window.PSCol26;
    if (CFG.cards26 && shell && typeof shell.cardHTML === 'function') {
      var html = shell.cardHTML(p, {compare: false});
      return html.replace(/(<span class="pname">)[^<]*(<\/span>)/, function (m, a, b) { return a + esc(name) + b; });
    }
    var isRender = p.images.source === 'render-drive';
    var hasMetals = !!p.images.byColor;
    var metal0 = defaultMetal(p);
    /* byColor holds {a,b,angles} objects on render-backed pieces and plain CDN URL
       strings on the rest. Both mean "this piece exists in this metal", so both are
       read here — reading only the object form is what made a color click on a
       CDN-backed card assign "undefined". */
    var cdnColor = (hasMetals && !isRender) ? (p.images.byColor[metal0] || p.images.single) : null;
    var frontSrc = isRender ? p.images.byColor[metal0].a : (cdnColor || p.images.single || p.images.cdn);
    var backSrc = isRender ? p.images.byColor[metal0].b : '';
    var shapes = '<div class="sdots" aria-label="Center stone shape: ' + (SHAPE_LABEL[p.shape] || 'not specified') + '">' +
      CARD_SHAPES.map(function (s) {
        return '<span class="sdot' + (s === p.shape ? ' is-sel' : '') + '" title="' + SHAPE_LABEL[s] + '">' +
          '<img src="' + dsAsset(s + '.png') + '" alt="" width="14" height="14" loading="lazy"></span>';
      }).join('') + '</div>';
    var metals = hasMetals ? '<div class="mdots">' + METALS.map(function (m) {
      return '<button type="button" class="mdot' + (m.key === metal0 ? ' is-sel' : '') + '" data-color="' + m.src + '"' +
        ' style="--m:' + m.fill + ';--mtext:' + m.ink + '" title="' + m.label + '" aria-label="' + m.label + '">' +
        '<span class="code">' + m.code + '</span></button>';
    }).join('') + '</div>' : '';
    var imgsAttr = hasMetals ? ' data-imgs="' + esc(JSON.stringify(p.images.byColor)) + '"' : '';
    var cdnAttr = isRender && p.images.cdn ? ' data-cdn="' + esc(p.images.cdn) + '"' : '';
    return '<article class="pcard pcard--grid" data-id="' + p.id + '"' + imgsAttr + '>' +
      '<a class="pimg" href="' + p.url + '">' +
        (isRender
          ? '<img class="pi-front" src="' + BLANK_IMG + '" data-dsrc="' + frontSrc + '"' + cdnAttr + ' alt="' + esc(name) + '">'
          : '<img class="pi-front" src="' + frontSrc + '" alt="' + esc(name) + '" loading="lazy">') +
        (backSrc ? '<img class="pi-back" src="' + BLANK_IMG + '" data-dsrc="' + backSrc + '"' + cdnAttr + ' alt="" aria-hidden="true">' : '') +
        '<span class="pcard-origin">' + ORIGIN_LABEL[p.origin === 'lab' ? 'lab' : 'natural'] + '</span>' +
      '</a>' +
      '<a class="pmeta" href="' + p.url + '">' +
        '<span class="pname">' + esc(name) + '</span>' +
        '<span class="pspec">' + specLine(p) + '</span>' +
        ratingHTML(p) + priceHTML(p) +
      '</a>' +
      shapes +
      metals +
    '</article>';
  }

  /* ---- Drive image loader ----------------------------------------------------
     Copied from the shell rather than shared, because its copy is inside its own
     closure and returns before this page needs it. lh3 refuses burst hotlinking, so
     images queue through a small concurrency window and each falls back
     lh3 -> Drive thumbnail -> the Shopify CDN copy before giving up. */
  var DRIVE_Q = [], DRIVE_ACTIVE = 0, DRIVE_MAX = 5;
  function driveFileId(u) { var m = /\/d\/([^=?&]+)/.exec(u || ''); return m ? m[1] : null; }
  function pumpDrive() {
    while (DRIVE_ACTIVE < DRIVE_MAX && DRIVE_Q.length) {
      var img = DRIVE_Q.shift();
      if (img.isConnected && img.getAttribute('data-dsrc')) loadDrive(img, 0);
    }
  }
  function loadDrive(img, attempt) {
    var base = img.getAttribute('data-dsrc');
    var id = driveFileId(base);
    var url = attempt === 0 ? base + '=w900'
            : (attempt === 1 && id) ? 'https://drive.google.com/thumbnail?id=' + id + '&sz=w900'
            : img.getAttribute('data-cdn') || '';
    if (!url) { pumpDrive(); return; }
    DRIVE_ACTIVE++;
    img.onload = function () { DRIVE_ACTIVE--; img.onload = img.onerror = null; img.removeAttribute('data-dsrc'); pumpDrive(); };
    img.onerror = function () {
      DRIVE_ACTIVE--; img.onload = img.onerror = null;
      if (attempt < 2) setTimeout(function () { loadDrive(img, attempt + 1); }, 600 * (attempt + 1));
      pumpDrive();
    };
    img.src = url;
  }
  var driveIO = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (en.isIntersecting) { driveIO.unobserve(en.target); DRIVE_Q.push(en.target); pumpDrive(); }
    });
  }, { rootMargin: '500px' }) : null;
  function hydrateDriveImgs(scope) {
    scope.querySelectorAll('img[data-dsrc]').forEach(function (img) {
      if (driveIO) driveIO.observe(img); else { DRIVE_Q.push(img); pumpDrive(); }
    });
  }

  /* How many columns the grid is actually showing. Read from the resolved template
     so one number drives both the first paint and every View More click, and the
     revealed block is always whole rows. */
  function gridCols(el) {
    var tpl = getComputedStyle(el).gridTemplateColumns || '';
    var rep = /repeat\(\s*(\d+)/.exec(tpl);
    if (rep) return parseInt(rep[1], 10);
    var n = tpl.split(' ').filter(function (s) { return s && s !== 'none'; }).length;
    return n || 4;
  }

  /* ---- the origin axis -------------------------------------------------------
     Three states, and ALL is the default: round is 1,491 lab against 1,192 natural,
     so opening on either branch would hide more than half the page, and the hub's
     whole claim is that it holds all of one shape. The state is also readable and
     writable in the URL (?origin=lab) so a shopper can send someone the branch they
     were looking at; the clean URL stays canonical. */
  var ORIGINS = ['all', 'natural', 'lab'];
  function readOrigin() {
    var m = /[?&]origin=(all|natural|lab)\b/.exec(location.search);
    return m ? m[1] : 'all';
  }
  var origin = readOrigin();

  function matches(p) { return origin === 'all' || (p && p.origin === origin); }
  function idsFor(fam) {
    return (fam.ids || []).filter(function (id) { return matches(BY_ID[id]); });
  }
  function totalFor(fam) {
    return origin === 'lab' ? fam.totalLab : (origin === 'natural' ? fam.totalNatural : fam.total);
  }
  /* The door for a shelf in the current state. A shape-scoped collection is preferred;
     the category collection is the fallback and is deliberately labelled without a
     number, because it is a wider set than the shelf above it. */
  function doorFor(fam) {
    var sa = fam.seeAll || {};
    var pick = (origin !== 'all' && sa[origin]) || sa.all || sa.category;
    if (!pick) return null;
    return { url: pick.url, title: pick.title, wide: pick === sa.category };
  }

  var sections = [].slice.call(document.querySelectorAll('.hubrow[data-family]'));
  var byKey = {};
  (HUB.families || []).forEach(function (f) { byKey[f.key] = f; });

  var shelves = sections.map(function (sec) {
    var fam = byKey[sec.getAttribute('data-family')];
    if (!fam) { console.warn('[shapehub] no data for shelf ' + sec.getAttribute('data-family')); return null; }
    var grid = sec.querySelector('.hubrow-grid');
    if (!grid) return null;
    var btn = sec.querySelector('.hubrow-more');
    var meta = sec.querySelector('.hubrow-meta');
    var bar = sec.querySelector('.hubrow-bar');
    var count = sec.querySelector('.hubrow-count');
    var all = sec.querySelector('.hubrow-all');
    var label = (HUBCFG.cardLabels && HUBCFG.cardLabels[fam.key]) || fam.cardLabel || fam.label;
    var ids = [], shown = 0;

    function update() {
      if (meta) meta.textContent = 'Showing ' + shown + ' of ' + num(ids.length);
      if (bar) bar.style.transform = 'scaleX(' + (ids.length ? shown / ids.length : 0) + ')';
      if (btn) btn.hidden = shown >= ids.length;
      if (count) {
        var t = totalFor(fam);
        /* The shelf holds up to `cap` of the family; the number a shopper reads is how
           many of that family the shape actually has, which is the honest one and the
           one the door goes on to. */
        count.textContent = num(t) + (t === 1 ? ' piece' : ' pieces');
      }
    }
    function reveal() {
      if (shown >= ids.length) return;
      var chunk = gridCols(grid) * ROWS;
      var slice = ids.slice(shown, shown + chunk);
      grid.insertAdjacentHTML('beforeend', slice.map(function (id) {
        var p = BY_ID[id];
        return p ? cardHTML(p, label) : '';
      }).join(''));
      shown += slice.length;
      hydrateDriveImgs(grid);
      update();
    }
    function apply() {
      ids = idsFor(fam);
      shown = 0;
      grid.innerHTML = '';
      /* A shelf with nothing in the chosen branch is removed rather than left standing
         empty under its heading: an empty "Round Ready to Ship — 0 pieces" shelf reads
         as a broken page, not as an answer. */
      sec.hidden = ids.length === 0;
      var door = doorFor(fam);
      if (all) {
        if (door) {
          all.hidden = false;
          all.href = door.url;
          all.innerHTML = (door.wide ? 'Shop all ' : 'See all ') + esc(door.title) + ' ' + ARROW;
        } else {
          all.hidden = true;
        }
      }
      if (!sec.hidden) reveal(); else update();
    }

    if (btn) btn.addEventListener('click', reveal);

    /* Metal swap. Every URL is already on the card in data-imgs, so a click assigns
       img.src and nothing is fetched twice; the back image follows the front so the
       hover still shows the same piece in the metal the shopper chose. */
    grid.addEventListener('click', function (e) {
      var md = e.target.closest('.mdot');
      if (!md) return;
      var card = md.closest('.pcard--grid');
      var imgs = JSON.parse(card.getAttribute('data-imgs') || '{}');
      var picked = imgs[md.getAttribute('data-color')];
      if (!picked) return;
      e.preventDefault();
      /* A Drive render is {a,b} and wants the =w900 size suffix; a CDN variant photo is
         already a whole URL and must be used as it stands. */
      var isDrive = typeof picked === 'object';
      var front = card.querySelector('.pi-front'), back = card.querySelector('.pi-back');
      if (front) { front.removeAttribute('data-dsrc'); front.src = isDrive ? picked.a + '=w900' : picked; }
      if (back && isDrive && picked.b) { back.removeAttribute('data-dsrc'); back.src = picked.b + '=w900'; }
      card.querySelectorAll('.mdot').forEach(function (d) { d.classList.toggle('is-sel', d === md); });
    });

    return { sec: sec, fam: fam, apply: apply };
  }).filter(Boolean);

  /* ---- the pills -------------------------------------------------------------
     Built here rather than in Liquid because they are in-page anchors carrying a
     product photograph: nothing a crawler needs, and one fewer thing to hand-type
     per shape. A pill for a shelf that is empty in the chosen branch goes with it. */
  var pillRow = document.getElementById('hub-pills');
  var pills = [];
  /* A row marked data-static was rendered by the section (the other nine shapes, owner
     review 2026-09-11) and is left alone: nothing here to build, hide or re-point. */
  if (pillRow && !pillRow.hasAttribute('data-static')) {
    var pillFams = (HUB.families || []).concat(HUB.pillsOnly || []);
    pillRow.innerHTML = pillFams.map(function (f) {
      var onPage = !!byKey[f.key] && shelves.some(function (s) { return s.fam.key === f.key; });
      var href = onPage ? '#sec-' + f.key : ((f.seeAll && (f.seeAll.all || f.seeAll.category) || {}).url || '');
      if (!href) return '';
      var thumb = f.pillThumb
        ? '<span class="thumb"><img src="' + esc(f.pillThumb) + '" width="256" height="256" alt="" loading="lazy"></span>'
        : '';
      return '<a class="relpill" data-family="' + f.key + '" href="' + esc(href) + '">' + thumb +
        '<span class="lab">' + esc(f.label) + '</span></a>';
    }).join('');
    pills = [].slice.call(pillRow.querySelectorAll('.relpill'));
    /* A combined-mode page is one grid with no families to jump between, so the pill row
       has nothing to hold. Hide the whole band rather than leave a strip of empty page
       between the switch and the picks. */
    var pillBand = pillRow.closest('.relc');
    if (pillBand) pillBand.hidden = pills.length === 0;
  }

  /* ---- Favorite Picks --------------------------------------------------------
     The shell renders all twelve curated picks once, six natural and six lab,
     interleaved so the ALL view opens on three of each. Switching branch hides the
     other six rather than re-rendering: the shell owns that markup, and a second
     copy of its card builder here is exactly how two renderers drift apart. */
  var pickOrigins = (CFG.picks && CFG.picks.origins) || [];
  var pickCards = [].slice.call(document.querySelectorAll('#picks-grid .pcard'));
  var picksBand = document.querySelector('.picks');
  /* Favorite Picks are drawn by the shared shell, which reads the product title straight
     through and so prints the store's "Ascher". Correcting it there would touch the ~105
     other pages that shell serves, so it is corrected here, on the names it already
     wrote. The same misspelling is live on the asscher COLLECTION pages and is worth a
     separate pass at the shell. */
  (function fixPickSpelling() {
    document.querySelectorAll('#picks-grid .pname').forEach(function (el) {
      var fixed = el.textContent;
      for (var s = 0; s < SPELLING.length; s++) fixed = fixed.replace(SPELLING[s][0], SPELLING[s][1]);
      if (fixed !== el.textContent) el.textContent = fixed;
    });
  })();
  function applyPicks() {
    if (!pickCards.length || pickCards.length !== pickOrigins.length) return;
    var shownCount = 0;
    pickCards.forEach(function (card, idx) {
      /* The row is a single horizontal scroller at every width now (owner ruling
         2026-09-01), so ALL shows every curated pick instead of hiding the back six —
         hiding was only there to stop a second grid row, which can no longer happen. */
      var keep = origin === 'all' ? true : pickOrigins[idx] === origin;
      card.hidden = !keep;
      if (keep) shownCount++;
    });
    /* A thin shape can have picks in one branch and none in the other: asscher's three
       are all natural, so the lab view had a FAVORITE PICKS heading over an empty row.
       Hide the band instead. An empty section reads as a broken page; a missing one
       reads as a page that has nothing to say there, which is the truth. */
    if (picksBand) picksBand.hidden = shownCount === 0;
  }

  /* ---- the switch ------------------------------------------------------------ */
  var counts = HUB.totals || {};
  var heroCount = document.getElementById('hero-count');
  var switchBtns = [].slice.call(document.querySelectorAll('.ohub-btn'));

  function applyAll() {
    shelves.forEach(function (s) { s.apply(); });
    /* Re-stripe after filtering, never in Liquid alone: the tint alternates over the
       shelves a shopper can SEE, and hiding one in a branch would otherwise leave two
       tinted shelves touching. */
    var seen = 0;
    shelves.forEach(function (s) {
      if (s.sec.hidden) return;
      s.sec.classList.toggle('hubrow--tint', seen % 2 === 1);
      seen++;
    });
    applyPicks();
    pills.forEach(function (pill) {
      var shelf = shelves.filter(function (s) { return s.fam.key === pill.getAttribute('data-family'); })[0];
      pill.hidden = !!(shelf && shelf.sec.hidden);
    });
    if (heroCount && counts[origin] != null) heroCount.textContent = num(counts[origin]);
    switchBtns.forEach(function (b) {
      var on = b.getAttribute('data-origin') === origin;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    document.documentElement.setAttribute('data-ps-origin', origin);
  }

  switchBtns.forEach(function (b) {
    var target = b.getAttribute('data-origin');
    if (counts[target] != null) {
      var badge = b.querySelector('.ohub-n');
      if (badge) badge.textContent = num(counts[target]);
    }
    b.addEventListener('click', function () {
      if (ORIGINS.indexOf(target) < 0 || target === origin) return;
      origin = target;
      /* replaceState, not pushState: the switch is a view of one page, and a back
         button that walks three filter states before leaving is a trap. */
      var url = target === 'all' ? location.pathname
              : location.pathname + '?origin=' + target;
      try { history.replaceState(null, '', url); } catch (e) { /* file:// review copy */ }
      applyAll();
      var shelvesTop = document.getElementById('hub-sections');
      if (shelvesTop && shelvesTop.getBoundingClientRect().top < 0) {
        shelvesTop.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  applyAll();

  /* The markup carries the shelf order so the H2s are crawlable; the data carries the
     sales ranking. If a rebuild reorders the families, say so out loud instead of
     letting the page quietly disagree with what sells. */
  var domOrder = sections.map(function (el) { return el.getAttribute('data-family'); }).join(',');
  var dataOrder = (HUB.families || []).map(function (f) { return f.key; }).join(',');
  if (domOrder !== dataOrder) {
    console.warn('[shapehub] shelf order differs from the sales ranking\n  page: ' + domOrder + '\n  data: ' + dataOrder);
  }
})();
